import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, Popover, TextField } from '@mui/material';
import { FaCalendarAlt, FaTimes } from 'react-icons/fa';
import { format, parse, isValid } from 'date-fns';

interface DateSearchHeaderProps {
  title: string;
  value: string; // yyyy-MM-dd format or empty string
  onChange: (dateStr: string) => void; // callback with yyyy-MM-dd or ''
}

/**
 * A standalone date search header component that:
 * - Validates dates properly (rejects invalid dates like 60.30.2026)
 * - Allows character-by-character editing (backspace removes one char)
 * - Has a clear (X) button on the input
 * - Has a calendar popover with "Очисти" and "Данас" buttons
 * - Closes when clicking outside
 * - Shows dd.MM.yyyy. placeholder format
 */
function DateSearchHeader({ title, value, onChange }: DateSearchHeaderProps) {
  // Display text is always in dd.MM.yyyy. format for the user
  const getDisplayText = (dateStr: string): string => {
    if (!dateStr) return '';
    try {
      const d = parse(dateStr, 'yyyy-MM-dd', new Date());
      return isValid(d) ? format(d, 'dd.MM.yyyy.') : '';
    } catch {
      return '';
    }
  };

  const [inputText, setInputText] = useState(getDisplayText(value));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Sync from external value changes (e.g. "Очисти" clears it)
  useEffect(() => {
    setInputText(getDisplayText(value));
  }, [value]);

  const validateAndNotify = (text: string) => {
    if (!text || text.trim() === '') {
      onChange('');
      return;
    }
    // Try to parse dd.MM.yyyy. or dd.MM.yyyy
    let d = parse(text, 'dd.MM.yyyy.', new Date());
    if (!isValid(d)) {
      d = parse(text, 'dd.MM.yyyy', new Date());
    }
    if (isValid(d)) {
      onChange(format(d, 'yyyy-MM-dd'));
    }
    // If invalid, don't notify — just keep the user's text for editing
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value;
    setInputText(newText);

    if (!newText || newText.trim() === '') {
      onChange('');
      return;
    }

    // Only validate when the user has typed enough characters for a full date
    // dd.MM.yyyy. = 11 chars, dd.MM.yyyy = 10 chars
    if (newText.length >= 10) {
      validateAndNotify(newText);
    }
  };

  const handleBlur = () => {
    // On blur, reset to the last valid value if current text is incomplete/invalid
    if (!inputText || inputText.trim() === '') {
      return;
    }
    let d = parse(inputText, 'dd.MM.yyyy.', new Date());
    if (!isValid(d)) {
      d = parse(inputText, 'dd.MM.yyyy', new Date());
    }
    if (!isValid(d)) {
      // Reset to current valid value
      setInputText(getDisplayText(value));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      if (!inputText || inputText.trim() === '') {
        setAnchorEl(null);
        return;
      }
      let d = parse(inputText, 'dd.MM.yyyy.', new Date());
      if (!isValid(d)) {
        d = parse(inputText, 'dd.MM.yyyy', new Date());
      }
      if (isValid(d)) {
        onChange(format(d, 'yyyy-MM-dd'));
      } else {
        // Invalid date on Enter — clear it
        onChange('');
        setInputText('');
      }
      setAnchorEl(null);
    } else if (e.key === 'Escape') {
      setAnchorEl(null);
    }
  };

  // Day-of-month helper grid
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  useEffect(() => {
    if (value) {
      try {
        const d = parse(value, 'yyyy-MM-dd', new Date());
        if (isValid(d)) {
          setViewYear(d.getFullYear());
          setViewMonth(d.getMonth());
        }
      } catch { /* ignore */ }
    }
  }, [value]);

  const open = Boolean(anchorEl);

  const selectDate = (year: number, month: number, day: number) => {
    const d = new Date(year, month, day);
    if (isValid(d)) {
      const formatted = format(d, 'yyyy-MM-dd');
      const display = format(d, 'dd.MM.yyyy.');
      onChange(formatted);
      setInputText(display);
    }
    setAnchorEl(null);
  };

  const handleClear = () => {
    onChange('');
    setInputText('');
    setAnchorEl(null);
  };

  const handleToday = () => {
    const todayStr = format(today, 'yyyy-MM-dd');
    const todayDisplay = format(today, 'dd.MM.yyyy.');
    onChange(todayStr);
    setInputText(todayDisplay);
    setAnchorEl(null);
  };

  // Generate calendar days
  const monthNames = [
    'Јануар', 'Фебруар', 'Март', 'Април', 'Мај', 'Јун',
    'Јул', 'Август', 'Септембар', 'Октобар', 'Новембар', 'Децембар',
  ];
  const dayNames = ['Пон', 'Уто', 'Сре', 'Чет', 'Пет', 'Суб', 'Нед'];

  const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfWeek = (year: number, month: number) => {
    const day = new Date(year, month, 1).getDay();
    // Convert Sunday=0 to Monday-based: Mon=0, Tue=1, ..., Sun=6
    return day === 0 ? 6 : day - 1;
  };

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfWeek(viewYear, viewMonth);

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  // Selected date for highlighting
  const getSelectedDay = (): number | null => {
    if (!value) return null;
    try {
      const d = parse(value, 'yyyy-MM-dd', new Date());
      if (isValid(d) && d.getFullYear() === viewYear && d.getMonth() === viewMonth) {
        return d.getDate();
      }
    } catch { /* ignore */ }
    return null;
  };
  const selectedDay = getSelectedDay();
  const isToday = (day: number) =>
    day === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();

  const calendarDays: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarDays.push(d);

  return (
    <div className="flex flex-col w-full" style={{ minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
      <span className="text-xs font-semibold mb-1">{title}</span>
      <div className="relative flex items-center w-full">
        <TextField
          variant="standard"
          fullWidth
          value={inputText}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="dd.MM.yyyy."
          sx={{
            width: '100%',
            '& .MuiInput-root': { width: '100%', paddingRight: '4px' },
            '& .MuiInput-input': { fontSize: '0.75rem', padding: '2px 0' },
            '& .MuiInput-underline:before': { borderBottomColor: '#81848f3d' },
            '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: '#1D4B8E' },
            '& .MuiInput-underline:after': { borderBottomColor: '#1D4B8E' },
          }}
          InputProps={{
            endAdornment: (
              <div className="flex items-center gap-0.5">
                {inputText && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClear();
                    }}
                    className="text-[#60626a] hover:text-[#303135] p-0 m-0 cursor-pointer"
                    style={{ fontSize: '0.7rem', lineHeight: 1, display: 'flex', alignItems: 'center' }}
                  >
                    <FaTimes />
                  </button>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAnchorEl(e.currentTarget as unknown as HTMLElement);
                  }}
                  className="text-[#60626a] hover:text-[#1D4B8E] p-0 m-0 cursor-pointer"
                  style={{ fontSize: '0.9rem', lineHeight: 1, display: 'flex', alignItems: 'center' }}
                >
                  <FaCalendarAlt />
                </button>
              </div>
            ),
          }}
        />
      </div>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableRestoreFocus
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            userSelect: 'none',
          },
        }}
      >
        <div style={{ padding: '8px', width: '260px' }}>
          {/* Month/Year navigation */}
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={prevMonth}
              className="text-[#60626a] hover:text-[#04438b] cursor-pointer text-sm font-bold px-2"
            >
              ‹
            </button>
            <span className="text-sm font-semibold text-[#303135]">
              {monthNames[viewMonth]} {viewYear}
            </span>
            <button
              onClick={nextMonth}
              className="text-[#60626a] hover:text-[#04438b] cursor-pointer text-sm font-bold px-2"
            >
              ›
            </button>
          </div>

          {/* Day-of-week headers */}
          <div className="grid grid-cols-7 gap-0 mb-1">
            {dayNames.map((dn) => (
              <div key={dn} className="text-center text-[10px] text-[#60626a] font-medium py-1">
                {dn}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-0">
            {calendarDays.map((day, idx) => (
              <div
                key={idx}
                onClick={() => day !== null && selectDate(viewYear, viewMonth, day)}
                className={`
                  text-center text-xs py-1 cursor-pointer rounded transition-colors
                  ${day === null ? '' : 'hover:bg-[rgba(4,67,139,0.1)]'}
                  ${day !== null && selectedDay === day ? 'bg-[#1D4B8E] text-white hover:bg-[#1D4B8E]' : ''}
                  ${day !== null && selectedDay !== day && isToday(day) ? 'font-bold text-[#04438b]' : 'text-[#303135]'}
                `}
                style={{ minHeight: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
            <Button
              size="small"
              onClick={handleClear}
              sx={{ textTransform: 'none', color: '#60626a' }}
            >
              Очисти
            </Button>
            <Button
              size="small"
              onClick={handleToday}
              sx={{ textTransform: 'none', color: '#04438b' }}
            >
              Данас
            </Button>
          </Box>
        </div>
      </Popover>
    </div>
  );
}

export default DateSearchHeader;
